import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, MapPin, Users, ArrowLeft, Check, ExternalLink, Video, Copy } from "lucide-react";
import { useApp } from "../context/AppContext";
import { api, assetUrl } from "../lib/api";
import { toast } from "sonner";

const card = { background: "var(--bg-cream-card)", borderColor: "var(--border-cream)" };
const inputCls = "w-full rounded-lg border px-3 py-2 text-sm bg-transparent";
export const eventTime = (e) => e.time || [e.start_time, e.end_time].filter(Boolean).join(" – ");

export default function EventDetail() {
  const { eventId } = useParams();
  const { user, rsvps, refreshRsvps } = useApp();
  const [e, setE] = useState(null);
  const [loading, setLoading] = useState(true);
  const [done, setDone] = useState(null);

  const load = async () => {
    try { const { data } = await api.get(`/content/events/${eventId}`); setE(data); } catch { setE(null); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-14 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>;
  if (!e) return (
    <div className="max-w-4xl mx-auto px-4 py-14 text-center">
      <div className="font-serif text-2xl mb-2" style={{ color: "var(--burgundy)" }}>This gathering is not available.</div>
      <Link to="/gather" className="ink-link text-sm" style={{ color: "var(--burgundy)" }}>Back to Gather →</Link>
    </div>
  );

  const av = e.availability || {};
  const mine = user ? rsvps.find((r) => r.event_id === e.id) : null;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 lg:py-16" data-testid="event-detail-page">
      <Link to="/gather" data-testid="event-back-link" className="inline-flex items-center gap-1 text-sm ink-link mb-6" style={{ color: "var(--burgundy)" }}><ArrowLeft className="w-4 h-4" /> Back to Gather</Link>
      <div className="rounded-3xl overflow-hidden border" style={card}>
        <div className="relative h-56 sm:h-72" style={{ background: "var(--teal-tint)" }}>
          {e.art_url && <img src={assetUrl(e.art_url)} alt={e.title} className="w-full h-full object-cover" />}
          <span className="absolute top-4 left-4 rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest" style={{ background: "rgba(84,28,43,0.9)", color: "var(--gold)" }}>{e.format}</span>
        </div>
        <div className="p-6 sm:p-10">
          <h1 className="font-serif text-3xl sm:text-4xl leading-tight" style={{ color: "var(--burgundy)" }} data-testid="event-title">{e.title}</h1>
          <div className="mt-4 flex flex-wrap gap-4 text-sm" style={{ color: "var(--text-muted)" }}>
            <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{e.date}{eventTime(e) ? ` · ${eventTime(e)}` : ""}{e.time_zone ? ` ${e.time_zone}` : ""}</span>
            {e.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{e.location}</span>}
            {av.label && <span className="inline-flex items-center gap-1" data-testid="event-availability"><Users className="w-4 h-4" />{av.label}</span>}
          </div>
          {e.description && <p className="mt-6 text-base leading-relaxed whitespace-pre-line" style={{ color: "var(--text-obsidian)" }}>{e.description}</p>}
          <div className="hairline my-8" />
          {done ? <Confirmation reg={done} onCancelled={() => { setDone(null); load(); refreshRsvps?.(); }} />
            : e.registration_mode === "external" && e.registration_url ? (
              <a data-testid="event-external-register-link" href={e.registration_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium" style={{ background: "var(--burgundy)", color: "var(--bg-cream)" }}>Register <ExternalLink className="w-3.5 h-3.5" /></a>
            ) : e.registration_mode === "none" ? (
              <p className="text-sm italic" style={{ color: "var(--text-muted)" }} data-testid="event-no-registration">No registration is needed — simply come as you are.</p>
            ) : mine ? (
              <div data-testid="event-already-registered" className="rounded-2xl border p-5" style={{ borderColor: "var(--gold)" }}>
                <div className="inline-flex items-center gap-2 font-medium" style={{ color: "var(--burgundy)" }}><Check className="w-4 h-4" /> You are {mine.status} for this gathering.</div>
                <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Manage it in <Link to="/my-temple" className="ink-link" style={{ color: "var(--burgundy)" }}>My Temple → My Gatherings</Link>.</p>
              </div>
            ) : <RegisterForm event={e} user={user} onDone={(r) => { setDone(r); load(); refreshRsvps?.(); }} />}
        </div>
      </div>
    </div>
  );
}

export function RegisterForm({ event: e, user, onDone }) {
  const full = e.availability?.full;
  const [f, setF] = useState({ first_name: (user?.name || "").split(" ")[0] || "", last_name: (user?.name || "").split(" ").slice(1).join(" ") || "", email: user?.email || "", phone: "", accommodation: "", marketing_consent: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const submit = async (ev) => {
    ev.preventDefault(); setBusy(true); setErr(null);
    try {
      const { data } = await api.post(`/content/events/${e.id}/register`, { ...f, join_waitlist: !!full });
      onDone(data);
    } catch (x) {
      const d = x?.response?.data?.detail;
      setErr(typeof d === "string" ? d : d?.message || "Registration could not be completed.");
    } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} data-testid="event-register-form">
      <div className="eyebrow mb-2">{full ? "Join the waitlist" : "Reserve a seat"}</div>
      <p className="text-sm mb-5" style={{ color: "var(--text-muted)" }}>{full ? "This gathering is full. Join the waitlist and we will hold your place in order." : "Registration is free. No Temple account is needed."}</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="reg-first-name-input" required placeholder="First name" value={f.first_name} onChange={(x) => set("first_name", x.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
        <input data-testid="reg-last-name-input" required placeholder="Last name" value={f.last_name} onChange={(x) => set("last_name", x.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
        <input data-testid="reg-email-input" required type="email" placeholder="Email address" value={f.email} onChange={(x) => set("email", x.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
        <input data-testid="reg-phone-input" placeholder="Phone (optional)" value={f.phone} onChange={(x) => set("phone", x.target.value)} className={inputCls} style={{ borderColor: "var(--border-cream)" }} />
      </div>
      <textarea data-testid="reg-accommodation-input" rows={2} placeholder="Accessibility or accommodation needs (optional)" value={f.accommodation} onChange={(x) => set("accommodation", x.target.value)} className={inputCls + " mt-3"} style={{ borderColor: "var(--border-cream)" }} />
      <label className="flex items-start gap-2 mt-4 text-sm" style={{ color: "var(--text-obsidian)" }}>
        <input data-testid="reg-consent-checkbox" type="checkbox" checked={f.marketing_consent} onChange={(x) => set("marketing_consent", x.target.checked)} className="mt-1" />
        Keep me connected with Temple of the Human Heart by email. <span style={{ color: "var(--text-muted)" }}>(optional)</span>
      </label>
      {err && <p data-testid="reg-error" className="text-sm mt-3" style={{ color: "var(--rose-dark)" }}>{err}</p>}
      <button data-testid="reg-submit-btn" disabled={busy} className="mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium disabled:opacity-60" style={{ background: full ? "var(--teal)" : "var(--burgundy)", color: "var(--bg-cream)" }}>
        {busy ? "Saving…" : full ? "Join waitlist" : "Reserve my seat"}
      </button>
    </form>
  );
}

export function Confirmation({ reg, onCancelled, manageMode }) {
  const ev = reg.event || reg.snapshot || {};
  const manageUrl = reg.manage_path ? `${window.location.origin}${reg.manage_path}` : null;
  const [busy, setBusy] = useState(false);
  const cancel = async () => {
    if (!window.confirm("Cancel this registration?")) return;
    setBusy(true);
    try {
      if (reg.manage_token) await api.post(`/content/registrations/manage/${reg.manage_token}/cancel`);
      else await api.post(`/me/registrations/${reg.id}/cancel`);
      toast("Registration cancelled"); onCancelled?.();
    } catch { toast.error("Could not cancel"); } finally { setBusy(false); }
  };
  const cancelled = reg.status === "cancelled";
  return (
    <div data-testid="event-confirmation" className="rounded-2xl border p-6" style={{ borderColor: cancelled ? "var(--border-cream)" : "var(--gold)" }}>
      <div className="eyebrow mb-2" style={{ color: reg.status === "waitlisted" ? "var(--teal)" : undefined }}>
        {cancelled ? "Registration cancelled" : reg.status === "waitlisted" ? `You are on the waitlist${reg.waitlist_position ? ` · position ${reg.waitlist_position}` : ""}` : "Your seat is reserved"}
      </div>
      <h2 className="font-serif text-2xl" style={{ color: "var(--burgundy)" }}>{ev.title}</h2>
      <div className="text-sm mt-2" style={{ color: "var(--text-muted)" }}>{ev.date}{eventTime(ev) ? ` · ${eventTime(ev)}` : ""}{ev.time_zone ? ` ${ev.time_zone}` : ""} · {ev.format}{ev.location ? ` · ${ev.location}` : ""}</div>
      <div className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>Registered as {reg.first_name} {reg.last_name} · {reg.email} · <span data-testid="reg-status" className="font-medium" style={{ color: "var(--burgundy)" }}>{reg.status}</span></div>
      {ev.virtual_url && reg.status === "confirmed" && (
        <a data-testid="reg-virtual-link" href={ev.virtual_url} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm border" style={{ borderColor: "var(--teal)", color: "var(--teal-deep)" }}><Video className="w-4 h-4" /> Join the virtual room</a>
      )}
      {manageUrl && !cancelled && (
        <div className="mt-5 rounded-lg p-3 text-xs" style={{ background: "var(--bg-cream)", color: "var(--text-muted)" }}>
          <div className="font-medium mb-1" style={{ color: "var(--text-obsidian)" }}>Keep this private link to view or cancel your registration:</div>
          <div className="flex items-center gap-2"><code className="break-all flex-1" data-testid="reg-manage-link">{manageUrl}</code>
            <button type="button" onClick={() => { navigator.clipboard?.writeText(manageUrl); toast("Link copied"); }} className="p-1"><Copy className="w-3.5 h-3.5" /></button></div>
          <div className="mt-1 italic">Email confirmations will be sent once Temple Communications is live — save this link for now.</div>
        </div>
      )}
      {!cancelled && <button data-testid="reg-cancel-btn" onClick={cancel} disabled={busy} className="mt-5 text-xs ink-link" style={{ color: "var(--rose-dark)" }}>Cancel my registration</button>}
      {!manageMode && <div className="mt-4"><Link to="/gather" className="text-xs ink-link" style={{ color: "var(--burgundy)" }}>← Back to Gather</Link></div>}
    </div>
  );
}
